import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import React from "react";
import RenderImage from "./RenderImage";
import { CarouselCardProps, CarouselCard as CarouselCardType } from "../../package-carousel.props";
import DtvModal from "@directv-digital/dds.ui.dtv-modal";
import CardModalContent from "../CardModalContent/CardModalContent";
import CardTopSection from "./CardTopSection";
import CardBottomSection from "./CardBottomSection";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";
import CardSvodsSection from "./CardSvodsSection";

const styles = {
  cardContainerStyle: {
    background:
      "radial-gradient(circle farthest-corner at bottom right, #0062E3, #121212)",
    display: "flex",
    flexDirection: "column",
    borderRadius: "20px",
    m: "8px",
    flow: "vertical",
    willChange: "transform", // INP Fix: Pre-promote to GPU layer for smoother animations
    transform: "scale(1)",
    transition: "transform 0.2s ease-in-out",
    "&:hover, &:focus, &:active": {
      cursor: "pointer",
      transform: "scale(1.05)",
      boxShadow: "0 2px 12px 0 rgba(0, 4, 67, 0.16), 0 1px 0 0 rgba(255, 255, 255, 0.30) inset, 0 -1px 0 0 rgba(0, 0, 0, 0.12) inset"
    },
    "&:focus": {
      outline: "1px solid #3885E1"
    }
  },
  imageSectionStyles: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    alignItems: "center",
    justifyContent: "center"
  },
  bodySectionStyles: {
    p: "16px",
    display: "inline",
    gap: "12px"
  },
  modalStyle: {
    maxHeight: "min-content",
    minWidth: {
      xs: "100%",
      sm: "600px",
      md: "900px"
    },
    maxWidth: {
      xl: "1028px",
      lg: "900px",
      md: "900px",
      sm: "600px",
      xs: "100%"
    }
  },
  modalBodyStyle: {
    height: {
      xs: "80vh",
      sm: "80vh"
    },
    maxHeight: {
      xs: "80vh",
      sm: "80vh"
    },
    p: {
      xs: "0px 20px 20px 18px",
      sm: "0px 32px 32px 32px",
      md: "0px 48px 32px 48px"
    },
    m: {
      xs: "0px",
      sm: "0px",
      md: "0px",
      lg: "0px",
      xl: "0px"
    },
    mb: {
      xs: "20px !important",
      sm: "32px !important",
      md: "32px!important"
    },
    borderRadius: "0px 0px 12px 12px",
    position: "relative",
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarGutter: "auto"
  }
};

// Generated by Copilot- Start - 2026-04-12 17:26:15
// AC3: Derive genre pack URL from CMS targeturl instead of hardcoded switch
// Normalizes CMS targeturl variants (targeturl.targeturl or targeturl.url)
const getGenrePackUrl = (card: CarouselCardType): string | undefined => card?.config?.targeturl?.targeturl || card?.config?.targeturl?.url || undefined;
// Generated by Copilot- End - 2026-04-12 17:26:15

const CarouselCard = (props: CarouselCardProps) => {
  const { card, index, cardWidth, myFreeConfig } = props;
  const isModalCard = card?.config?.type === "modal";
  // null = never opened → the DtvModal subtree is not mounted at all.
  // Mounting one closed DtvModal per card (each with its own state + effects)
  // was pure overhead for non-modal cards and never-clicked modal cards.
  // null = modal subtree fully unmounted (initial state and after close).
  // DtvModal has no exit transition, so unmount-on-close is visually
  // identical to open={false} while releasing the modal instance.
  const [isModalOpen, setIsModalOpen] = React.useState<boolean | null>(null);
  // Cards render under index keys, so an instance can be reused for a
  // different card after a list change — drop stale open state if the card
  // stops being a modal card.
  React.useEffect(() => {
    if (!isModalCard) {
      setIsModalOpen(null);
    }
  }, [isModalCard]);
  try {
    const handleCardClick = () => {
      switch (card?.config?.type) {
        case "modal":
          // Non-urgent render keeps the click handler fast (INP) while the
          // heavy modal subtree (channel grid, tooltips) mounts.
          React.startTransition(() => setIsModalOpen(true));
          break;
        case "redirect":
          window.open(
            card?.config?.targeturl?.targeturl || "/shop/build/configure",
            card?.config?.targeturl?.target || "_self"
          );
          break;
        default:
          // Analytics handled declaratively via getAnalyticsAttrs data-attrs on the card container.
          // requestIdleCallback deferral is no longer needed since there is no synchronous analytics
          // call blocking the main thread before navigation.
          window.open("/shop/build/configure", "_self");
      }
    };

    const genrePackUrl = getGenrePackUrl(card);

    return (
      <>
        <DtvBox
          data-testid="carousel-card-root"
          role="button"
          tabIndex={0}
          {...getAnalyticsAttrs(
            card?.title?.content,
            "genrepack_carousel",
            card?.config?.type === "redirect" && card?.config?.targeturl?.targeturl ?
              card?.config?.targeturl?.targeturl :
              card?.config?.type === "modal" ?
              `modal_${(card?.modalData?.title?.content || card?.title?.content || "")?.toString()?.toLowerCase()}` :
              typeof window !== "undefined" ?
              window.location.href :
              ""
          )}
          itemScope
          aria-label={card?.ariaLabel || card?.title?.content}
          itemType="https://schema.org/Product"
          sx={{
              ...styles.cardContainerStyle,
              width: cardWidth,
              minWidth: cardWidth,
              maxWidth: cardWidth,
              ...(card?.background && { background: card.background }),
              ...(card?.boxShadow && { boxShadow: card.boxShadow })
            }}
            onClick={handleCardClick}
            onKeyDown={event => {
            if (event.key === "Enter") {
 handleCardClick();
}
          }}
          >
          {/* Schema.org meta tags */}
          <meta content={card?.title?.content || ""} itemProp="name" />
          <meta content="https://www.directv.com/dtvassets/logos/DIRECTV-1200x1200.png" itemProp="image" />
          <meta content={typeof card.subHeading === "string" ? card.subHeading : card.subHeading?.content || ""} itemProp="description" />
          {/* Brand */}
          <DtvBox itemScope data-testid="carousel-card-brand" itemProp="brand" itemType="https://schema.org/Brand">
            <meta content="DIRECTV" itemProp="name" />
          </DtvBox>
          {/* Offer */}
          {/* Generated by Copilot- Start - 2026-04-12 17:26:15 */}
          {/* AC3: Schema.org offer URL from CMS targeturl — guards absolute URLs */}
          <DtvBox itemScope data-testid="carousel-card-offers" itemProp="offers" itemType="https://schema.org/Offer">
            <meta itemProp="url" content={(() => {
              const url = card?.config?.targeturl?.targeturl || card?.config?.targeturl?.url;
              if (!url) {
 return "";
}
              return url.startsWith("http") ? url : `https://www.directv.com${url}`;
            })()} />
          {/* Generated by Copilot- End - 2026-04-12 17:26:15 */}
            <meta content="USD" itemProp="priceCurrency" />
            <meta content={card?.priceText?.content ? card.priceText.content.replace(/\$|\/mo.*/g, "") : ""} itemProp="price" />
            <meta content="https://schema.org/InStock" itemProp="availability" />
          </DtvBox>
          {/* Top Section */}
          <CardTopSection
            index={index}
            subHeading={card.subHeading}
            title={card.title}
            topper={card.topper}
          />
          {/* svods section */}
          <CardSvodsSection svods={card?.svods || []} />
          {/* Images Section */}
          <DtvBox
            data-testid="carousel-card-image-section"
            id={`carousel-card-image-section-${index}`}
            sx={{ ...styles.imageSectionStyles }}
          >
            {/* check for array */}
            {/* Best-effort lazy hint for cards beyond the initial set; in the
                translateX Carousel variant the browser may still fetch eagerly */}
            {Array.isArray(card?.images) && card?.images?.length > 0 ?
              card?.images?.map((image, idx) => (
                  <RenderImage key={idx} {...image} imgLoading={index > 3 ? "lazy" : undefined} />
                )) :
              [card?.images]?.map((image, idx) => (
                  <RenderImage key={idx} {...image} imgLoading={index > 3 ? "lazy" : undefined} />
                ))}
          </DtvBox>
          {/* Body Section */}
          {card?.bodyText?.content && (
            <DtvBox
              data-testid="carousel-card-body-section"
              id={`carousel-card-body-section-${index}`}
              sx={{ ...styles.bodySectionStyles }}
            >
              {card?.bodyText?.content && (
                <DtvText
                  {...card.bodyText}
                  content={`${card.bodyText?.content  }&nbsp;`}
                  display={"inline"}
                />
              )}
              {card?.bodyText?.spanText?.content && (
                <DtvText
                  {...card.bodyText?.spanText}
                  sx={{
 display: "inline",
textDecoration: "underline"
}}
                />
              )}
            </DtvBox>
          )}
          {/* Bottom Section */}
          <CardBottomSection
            cardQueryParam={card?.queryparam}
            cardType={card?.config?.type}
            index={index}
            offerRibbon={card?.offerRibbon}
            pkgType={card?.pkgType}
            priceText={card?.priceText}
            rightArrow={card?.rightArrow}
            strikeThroughPrice={card?.strikeThroughPrice}
            targetUrl={genrePackUrl}
            title={card?.title?.content}
          />
        </DtvBox>
        {isModalCard && isModalOpen !== null && (
          <DtvModal
            handleClose={() => setIsModalOpen(null)}
            open={!!isModalOpen}
            modalBodyStyle={{
              ...styles.modalBodyStyle
            }}
            modalStyle={{
              background: card?.modalData?.background || "#FBFBFB",
              ...styles.modalStyle
            }}
            onClose={(_event, reason) => {
              if (reason === "backdropClick") {
                return;
              }
              setIsModalOpen(null);
            }}
          >
            <CardModalContent
              {...card?.modalData}
              config={card?.config}
              myFreeConfig={myFreeConfig}
            />
          </DtvModal>
        )}
      </>
    );
  } catch (error) {
    return <ErrorDisplay component="CarouselCard" error={error} />;
  }
};

export default React.memo(CarouselCard);
